import React, { type MutableRefObject, useEffect, useRef } from 'react';

import TableEmptyRowExtended from '@components/common/TableEmptyRowExtended/TableEmptyRowExtended';

import { useDebouncedValue } from '@hooks/useDebouncedValue';
import { useIntersectionObserver } from '@hooks/useIntersectionObserver';
import useTranslation from '@hooks/useTranslation';

import { useWhatsNewListInfiniteQuery } from '../../services/useWhatsNewListInfiniteQuery';
import { useWhatsNewMarkViewedMutation } from '../../services/useWhatsNewMarkViewedMutation';
import { selectWhatsNewIsFavorite } from '../../store/useWhatsNewStore';
import NewItem from '../NewItem/NewItem';
import NewItemSkeleton from '../NewItem/NewItemSkeleton';
import style from './style.module.scss';

interface IProps {
  searchValue: string;
}

const NewsList: React.FC<IProps> = ({ searchValue }) => {
  const { t } = useTranslation();
  const [ref, isInViewport] = useIntersectionObserver();
  const { mutate: whatsNewMarkViewedMutate } = useWhatsNewMarkViewedMutation();

  const isFavorite = selectWhatsNewIsFavorite();

  const [debouncedValue] = useDebouncedValue(searchValue, 500);

  const { data, isFetching, hasNextPage, isLoading, fetchNextPage } = useWhatsNewListInfiniteQuery({
    searchValue: debouncedValue,
    isFavorite,
  });

  const viewedRef: MutableRefObject<Set<number>> = useRef(new Set());

  const whatsNewList = data?.pages?.flatMap((page) => page.whatsNewList) ?? [];

  useEffect(() => {
    if (isInViewport && !isFetching && hasNextPage) {
      fetchNextPage();
    }
  }, [isInViewport, isFetching]);

  useEffect(() => {
    return () => {
      if (viewedRef.current.size) {
        whatsNewMarkViewedMutate({ whatsNewIds: Array.from(viewedRef.current) });
        viewedRef.current.clear();
      }
    };
  }, []);

  const getEmptyMessage = () => {
    if (searchValue) return t('NO NEWS WERE FOUND');

    if (!isFavorite) {
      return t('No news yet');
    } else {
      return t('No favorites exist');
    }
  };

  return (
    <div>
      {isLoading ? (
        <NewItemSkeleton />
      ) : whatsNewList.length ? (
        whatsNewList.map((item) => (
          <NewItem key={item.newsId} item={item} setViewedNews={(id) => viewedRef.current.add(id)} />
        ))
      ) : (
        <table className={style.tableBodyRowEmpty}>
          <tbody>
            <TableEmptyRowExtended text={getEmptyMessage()} />
          </tbody>
        </table>
      )}
      <div ref={ref} />
    </div>
  );
};

export default NewsList;
